import { useState } from "react";
import { format } from "date-fns";

import { getNextSaturday, getPreviousSunday } from "@utils/general";
import FlowbiteDatepickerInLine from "@components/DatepickerInLine";
import  Button  from "@components/Button";
import HeadlessComboboxInLine from "@components/ComboboxInLine";
import { generateCSV, generateExcel } from "@service/payrollApiService";
import Spinner from "@components/Spinner";

export interface ICSVSelect {
  id: number;
  value: string;
}

export function PayRoll(): JSX.Element {
  const [sendingExcel, setSendingExcel] = useState<boolean>(false);
  const [sendingCSV, setSendingCSV] = useState<boolean>(false);
  const [start_date, setStartDate] = useState<Date | null>(getPreviousSunday(null,-1));
  const [end_date, setEndDate] = useState<Date | null>(getNextSaturday(new Date(),-1));
  const [optionsCSV] = useState<ICSVSelect[]>([
    { id: 1, value: "Check" },
    { id: 2, value: "Direct Deposit" },
    { id: 3, value: "All" },
  ]);
  const [selectedCSV, setSelectedCSV] = useState<ICSVSelect | null>({ id: 1, value: "Check" });

  function handleStartDateChange(date: Date) {
    if(date.getDay() !== 0) {
      alert("Please select a Sunday.");
      return;
    }
    setStartDate(date);
    const nextSaturday = (getNextSaturday(date? date : new Date()));
    setEndDate(nextSaturday);
  }

  function handleEndDateChange(date: Date | null) {
    if(!date) {
      setEndDate(null);
      return;
    }
    if(date.getDay() !== 6) {
      alert("Please select a Saturday.");
      return;
    }
    const previousSunday = getPreviousSunday(date);
    handleStartDateChange(previousSunday);
  }

  function handleChangeCSV(value: ICSVSelect) {
    setSelectedCSV(value);
  }

  async function handleSubmitExcel() {
    try {
      if(!start_date || !end_date) {
        alert("Please select a valid start and end date.");
        return;
      }
      setSendingExcel(true);
      const sendData = {
        start_date: format(start_date, "yyyy-MM-dd"),
        end_date: format(end_date, "yyyy-MM-dd"),
      }
      const response = await generateExcel(sendData);
      const url = window.URL.createObjectURL(response.data as any);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", response.headers["content-disposition"]?.split("filename=")[1] ?? "payroll.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSendingExcel(false);
      alert("PayRoll Excel successfully generated!");

    } catch (err) {
      setSendingExcel(false);
      console.error("PayRoll error:", err);
      alert(`PayRoll Excel failed. ${err}`);
    }
  }
  async function handleSubmitCSV() {
    try {
      if(!start_date || !end_date) {
        alert("Please select a valid start and end date.");
        return;
      }
      setSendingCSV(true);
      const sendData = {
        filter: selectedCSV?.value,
        start_date: format(start_date, "yyyy-MM-dd"),
        end_date: format(end_date, "yyyy-MM-dd"),
      }
      const response = await generateCSV(sendData);
      const url = window.URL.createObjectURL(response.data as any);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", response.headers["content-disposition"]?.split("filename=")[1] ?? "payroll.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSendingCSV(false);
      alert("PayRoll CSV successfully generated!");

    } catch (err) {
      setSendingCSV(false);
      console.error("PayRoll error:", err);
      alert(`PayRoll CSV failed. ${err}`);
    }
  }

  return (
    <>
      <div className="tw-flex tw-mb-2 tw-h-[42vh]">
        <div className="tw-flex tw-min-w-[380px] tw-flex-col">
          <h4 className="Subheader tw-mb-3">Payroll Generator</h4>
          <div className="tw-flex tw-flex-row tw-items-center tw-mb-3 tw-ml-4 tw-mt-4">
            <span className="tw-w-8">Start:</span>
            <FlowbiteDatepickerInLine 
              name={undefined} 
              value={start_date} 
              handleOnChange={handleStartDateChange} 
              handleOnBlur={undefined} 
              className="tw-w-[350px] tw-ml-2" 
              />
          </div>
          <div className="tw-flex tw-flex-row tw-items-center tw-mb-3 tw-ml-4">
            <span className="tw-w-8">End:</span>
            <FlowbiteDatepickerInLine 
              name={undefined} 
              value={end_date} 
              handleOnChange={handleEndDateChange} 
              handleOnBlur={undefined} 
              className="tw-w-[350px] tw-ml-2" 
              />
          </div>
          <div className="tw-flex tw-justify-center tw-mt-3 tw-ml-14">
            <Button
                disabled={sendingExcel}
                className="md:tw-w-[50%]"
                onClick={handleSubmitExcel}
                >
                {sendingExcel ? <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center" /> : 'Excel'}
            </Button>
          </div>
          <div className="tw-flex tw-flex-row tw-items-center tw-mb-3 tw-ml-4 tw-mt-8">
            <span className="tw-w-8">CSV:</span>
            <HeadlessComboboxInLine
              value={optionsCSV ? optionsCSV[0] : ""} 
              name="csv"
              options={optionsCSV}
              onChange={handleChangeCSV}
              placeholder="Select CSV type"
              errorMsg=""
              className="tw-w-[350px] tw-ml-2" 
              data-testid={`${selectedCSV?.value}-combobox`}
              disabled={false}
              />            
          </div>
          <div className="tw-flex tw-justify-center tw-mt-3 tw-ml-14">
            <Button
                disabled={sendingCSV}
                className="md:tw-w-[50%]"
                onClick={handleSubmitCSV}
                >
               {sendingCSV ? <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center" /> : 'CSV'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}